import { Box, Card } from '@ff-it/ui';
import { Fragment } from 'react';
import type { ActionContext, CatalogAction, SelectionState, TargetType } from './types';
import { TitleContainer } from 'components/TitleContainer';
import { ActionOption, DoNothingOption } from './ActionOption';
import { RelatedEntites } from './RelatedEntities';

type ActionContainerProps = {
  actionContext: ActionContext;
  selectionState: SelectionState;
  setSelectionState: (newState: SelectionState) => void;
  cascading?: boolean;
};

export function ActionContainer({
  actionContext,
  selectionState,
  setSelectionState,
  cascading = false,
}: ActionContainerProps) {
  const isSelected = selectionState?.id === actionContext.id && selectionState?.model === actionContext.model;

  const handleActionSelect = (action: CatalogAction) => {
    setSelectionState({
      id: actionContext.id,
      model: actionContext.model,
      action,
      target: undefined,
      cascade: null,
    });
  };

  const handleTargetChange = (target: TargetType | undefined) => {
    if (isSelected) {
      setSelectionState({ ...selectionState, target });
    }
  };

  const handleCascadeChange = (cascade: SelectionState) => {
    if (isSelected) {
      setSelectionState({ ...selectionState, cascade });
    }
  };

  return (
    <Card className="mb-0">
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" mb="2">
        <TitleContainer title={actionContext.repr} subtitle={actionContext.model} size="md" />
        <RelatedEntites related={actionContext.related} />
      </Box>
      {actionContext.action_options.map((optionGroup, groupIdx) => {
        const { actions, cascade } = optionGroup;

        return (
          <Box key={groupIdx}>
            {actions.map((actionAvailability) => {
              const isActionSelected = isSelected && selectionState?.action === actionAvailability.action;

              return (
                <Fragment key={actionAvailability.action}>
                  <ActionOption
                    {...actionAvailability}
                    model={actionContext.model}
                    isSelected={isActionSelected}
                    onSelect={() => handleActionSelect(actionAvailability.action)}
                    target={isActionSelected ? selectionState?.target : undefined}
                    onTargetChange={handleTargetChange}
                  />
                  {/* Show cascade if an action in this group is selected */}
                  {isActionSelected && cascade.length > 0 && (
                    <Box ml="6" mb="4">
                      <Box textSize="xs" fontWeight="semibold" color="gray500">
                        Related entities
                      </Box>
                      {cascade.map((cascadeContext, idx) => (
                        <ActionContainer
                          key={idx}
                          actionContext={cascadeContext}
                          selectionState={selectionState?.cascade}
                          setSelectionState={handleCascadeChange}
                          cascading
                        />
                      ))}
                    </Box>
                  )}
                </Fragment>
              );
            })}
            {cascading && (
              <DoNothingOption isSelected={!selectionState?.action} onSelect={() => setSelectionState(null)} />
            )}
          </Box>
        );
      })}
    </Card>
  );
}
